import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { portaleAPI } from '../utils/api';
import { useCartStore } from '../stores/cartStore';
import { CatalogProduct } from '../types';
import {
  MagnifyingGlassIcon,
  ShoppingCartIcon,
  CheckIcon,
  CubeIcon,
} from '@heroicons/react/24/outline';
import { formatCurrency } from '../utils/constants';
import toast from 'react-hot-toast';

const Catalog: React.FC = () => {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [categoria, setCategoria] = useState('');
  const [marca, setMarca] = useState('');
  const limit = 20;

  const addItem = useCartStore((s) => s.addItem);
  const cartItems = useCartStore((s) => s.items);

  const { data, isLoading } = useQuery({
    queryKey: ['catalog', page, search, categoria, marca],
    queryFn: () =>
      portaleAPI.getCatalog({
        page,
        limit,
        search: search || undefined,
        categoria: categoria || undefined,
        marca: marca || undefined,
      }),
  });

  const { data: categorieData } = useQuery({
    queryKey: ['catalog-categorie'],
    queryFn: () => portaleAPI.getCategorie(),
  });

  const { data: marcheData } = useQuery({
    queryKey: ['catalog-marche'],
    queryFn: () => portaleAPI.getMarche(),
  });

  const products = data?.data?.data || [];
  const pagination = data?.data?.pagination;
  const categorie = categorieData?.data || [];
  const marche = marcheData?.data || [];

  const isInCart = (productId: string) =>
    cartItems.some((i) => i.prodotto_id === productId);

  const handleAddToCart = (product: CatalogProduct) => {
    addItem(product);
    toast.success(`${product.nome} aggiunto al carrello`);
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Catalogo Prodotti</h1>
        <p className="text-gray-600 mt-1">Sfoglia i prodotti e aggiungili al carrello</p>
      </div>

      {/* Search and Filters */}
      <div className="bg-white shadow rounded-lg p-4">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Cerca prodotti..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
          </div>

          <select
            value={categoria}
            onChange={(e) => { setCategoria(e.target.value); setPage(1); }}
            className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value="">Tutte le categorie</option>
            {categorie.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>

          <select
            value={marca}
            onChange={(e) => { setMarca(e.target.value); setPage(1); }}
            className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
          >
            <option value="">Tutte le marche</option>
            {marche.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </form>
      </div>

      {/* Products Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="bg-white shadow rounded-lg p-4 animate-pulse">
              <div className="bg-gray-200 h-48 rounded-md mb-4" />
              <div className="bg-gray-200 h-4 rounded w-3/4 mb-2" />
              <div className="bg-gray-200 h-3 rounded w-1/2 mb-4" />
              <div className="bg-gray-200 h-8 rounded" />
            </div>
          ))}
        </div>
      ) : products.length === 0 ? (
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <CubeIcon className="mx-auto h-12 w-12 text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun prodotto trovato</h3>
          <p className="text-gray-500">Prova a modificare i filtri di ricerca</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {products.map((product) => (
            <div key={product.id} className="bg-white shadow rounded-lg overflow-hidden flex flex-col">
              {/* Image */}
              <div className="h-48 bg-gray-100 flex items-center justify-center">
                {product.immagine_default_url ? (
                  <img
                    src={product.immagine_default_url}
                    alt={product.nome}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <CubeIcon className="h-16 w-16 text-gray-300" />
                )}
              </div>

              {/* Info */}
              <div className="p-4 flex-1 flex flex-col">
                <h3 className="text-sm font-semibold text-gray-900 line-clamp-2 mb-1">
                  {product.nome}
                </h3>
                <div className="flex items-center gap-2 text-xs text-gray-500 mb-2">
                  {product.categoria && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                      {product.categoria}
                    </span>
                  )}
                  {product.marca && (
                    <span className="text-gray-400">{product.marca}</span>
                  )}
                </div>

                {product.descrizione && (
                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">{product.descrizione}</p>
                )}

                <div className="mt-auto">
                  {product.prezzo_finale != null && (
                    <div className="mb-3">
                      {product.prezzo_unitario != null && product.prezzo_finale < product.prezzo_unitario ? (
                        <div className="flex items-baseline gap-2">
                          <span className="text-lg font-bold text-primary-600">
                            {formatCurrency(product.prezzo_finale)}
                          </span>
                          <span className="text-sm text-gray-400 line-through">
                            {formatCurrency(product.prezzo_unitario)}
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-gray-900">
                          {formatCurrency(product.prezzo_finale)}
                        </span>
                      )}
                      <span className="text-xs text-gray-400">/{product.unita_misura}</span>
                    </div>
                  )}

                  {isInCart(product.id) ? (
                    <button
                      disabled
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md bg-green-50 text-green-700 border border-green-200"
                    >
                      <CheckIcon className="h-4 w-4" />
                      Nel carrello
                    </button>
                  ) : (
                    <button
                      onClick={() => handleAddToCart(product)}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors"
                    >
                      <ShoppingCartIcon className="h-4 w-4" />
                      Aggiungi
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination && pagination.pages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Precedente
            </button>
            <button
              onClick={() => setPage(Math.min(pagination.pages, page + 1))}
              disabled={page >= pagination.pages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
            >
              Successivo
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <p className="text-sm text-gray-700">
              Mostrando da <span className="font-medium">{(page - 1) * limit + 1}</span> a{' '}
              <span className="font-medium">{Math.min(page * limit, pagination.total)}</span> di{' '}
              <span className="font-medium">{pagination.total}</span> prodotti
            </p>
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Precedente
              </button>
              {[...Array(Math.min(pagination.pages, 7))].map((_, i) => {
                const pageNum = i + 1;
                return (
                  <button
                    key={pageNum}
                    onClick={() => setPage(pageNum)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      pageNum === page
                        ? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
                        : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                disabled={page >= pagination.pages}
                className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Successivo
              </button>
            </nav>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalog;
